import React, { useState } from "react";
import { Layout, Menu, Flex, Button } from "antd";
import type { MenuProps } from "antd";
import {
  BarChartOutlined,
  QrcodeOutlined,
  DashboardOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Page from "./Page";
import { useAntdTheme } from "../../hooks";

const { Sider, Content } = Layout;

const ManagerLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const [collapsed, setCollapsed] = useState(false);

  // Determine page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === "/manager") return t("manager_dashboard");
    if (path === "/manager/stats") return t("statistics");
    if (path === "/manager/qr-code") return t("in_store_qr_code");
    return t("manager_dashboard");
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "/manager",
      icon: <DashboardOutlined />,
      label: t("manager_dashboard"),
    },
    {
      type: "divider",
    },
    {
      key: "data-group",
      label: t("data_analytics"),
      type: "group",
      children: [],
    },
    {
      key: "/manager/stats",
      icon: <BarChartOutlined />,
      label: t("statistics"),
    },
    {
      type: "divider",
    },
    {
      key: "settings-group",
      label: t("settings"),
      type: "group",
      children: [],
    },
    {
      key: "/manager/qr-code",
      icon: <QrcodeOutlined />,
      label: t("in_store_qr_code"),
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    if (key === "/manager") {
      navigate("/manager");
    } else if (key.startsWith("/manager/")) {
      navigate(key);
    }
  };

  return (
    <Page title={getPageTitle()} noCard={true}>
      <Layout style={{ minHeight: "100%", background: "transparent" }}>
        {/* Sidebar */}
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          width={280}
          collapsedWidth={80}
          trigger={null}
          style={{
            background: token.colorBgContainer,
            borderRight: `1px solid ${token.colorBorderSecondary}`,
            boxShadow: token.boxShadowTertiary,
          }}
        >
          {/* Collapse Toggle */}
          <Flex
            justify={collapsed ? "center" : "flex-end"}
            style={{
              padding: token.paddingMD,
              borderBottom: `1px solid ${token.colorBorderSecondary}`,
            }}
          >
            <Button
              type="text"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => setCollapsed(!collapsed)}
              style={{
                fontSize: token.fontSizeLG,
                color: token.colorPrimary,
              }}
            />
          </Flex>

          {/* Menu */}
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            onClick={handleMenuClick}
            items={menuItems}
            style={{
              border: "none",
            }}
          />
        </Sider>

        {/* Main Content */}
        <Content style={{ padding: token.paddingMD }}>
          <div
            style={{
              background: token.colorBgContainer,
              borderRadius: token.borderRadiusLG,
              boxShadow: token.boxShadowTertiary,
              padding: token.paddingLG,
            }}
          >
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Page>
  );
};

export default ManagerLayout;
